import _ from 'lodash';
import * as members from '../../privates/privateMembers';
import effectValidation from '../../privates/validations/animations/effectValidation';
import {EFFECTS} from '../../privates/constants';
import {setAsyncFunctions} from '../../privates/utils/asyncDecorator';
import {setStageUnfriendlyFunctions} from '../../privates/utils/livePreview';
import platformUtils from '../../privates/platformUtils';
//TODO WEED-8265
// import * as postMessage from '../../privates/postMessage';

/**
 * @mixin HiddenMixin
 * @summary Provides functionality for elements that can be hidden.
 * @description
 *  To learn about the behavior of a hidden element,
 *  see the [`hidden`](#hidden) property.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  let isHidden = $w("#HiddenMixin").hidden;
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `HiddenMixin`. 
 * 
 *  ```javascript
 *  let isHidden = $w("#myElement").hidden;
 *  ```
 *  
 * @memberof $w
 */

function changeElementVisibility(RMI, compId, isHidden, cb) {
    RMI.setProps(compId, {isHidden}, cb);
}

//TODO WEED-8265
// function resetAnimationAndChangeElementVisibility(RMI, compId, isHidden, cb) {
//     postMessage.sendMessage(
//         postMessage.Intent.WIX_CODE_SITE_API,
//         postMessage.MessageTypes.RESET_BEHAVIORS,
//         {compId},
//         changeElementVisibility.bind(null, RMI, compId, isHidden, cb)
//     );
// }
//
// function shouldOverrideEditorPresets(effectName, effectOptions) {
//     return _.isNil(effectName) && _.isEmpty(effectOptions);
// }

class HiddenMixin {

    /**
     * @function hide
     * @summary Hides the element and sets its `hidden` property
     *  to `true`, using an effect if specified.
     * @description
     *  The `hide()` function hides the element and returns a Promise that is
     *  resolved when the effect is complete and the element's [`hidden`](#hidden)
     *  property has been set to `true`.
     *
     *  To learn about the behavior of a hidden element,
     *  see the [`hidden`](#hidden) property.
     *
     *  You can optionally apply an effect when hiding the element by providing
     *  an `effectName` value. You can also customize the effect by providing the
     *  optional `effectOptions` object.
     *
     *  Effects:
     *
     *  + `"arc"`
     *  + `"bounce"`
     *  + `"fade"`
     *  + `"flip"`
     *  + `"float"`
     *  + `"fly"`
     *  + `"fold"`
     *  + `"glide"`
     *  + `"puff"`
     *  + `"roll"`
     *  + `"slide"`
     *  + `"spin"`
     *  + `"turn"`
     *  + `"zoom"`
     *
     *
     *  You can also hide an element
     *  when the page loads by using the Properties and Events panel in the Editor.
     * @param {String} [effectName] The name of the
     *  effect to play when hiding the item.
     * @param {$w.EffectOptions~ArcEffectOptions | $w.EffectOptions~BounceEffectOptions | $w.EffectOptions~FadeEffectOptions | $w.EffectOptions~FlipEffectOptions | $w.EffectOptions~FloatEffectOptions | $w.EffectOptions~FlyEffectOptions | $w.EffectOptions~FoldEffectOptions | $w.EffectOptions~GlideEffectOptions | $w.EffectOptions~PuffEffectOptions | $w.EffectOptions~RollEffectOptions | $w.EffectOptions~SlideEffectOptions | $w.EffectOptions~SpinEffectOptions | $w.EffectOptions~TurnEffectOptions | $w.EffectOptions~ZoomEffectOptions} [effectOptions] The effect's options.
     * @returns {Promise}
     * @fulfill {void} When the effect is complete and the element's `hidden` property has been set to `true`.
     * @see [show( )](#show)
     * @see [hidden](#hidden)
     * @see [collapse( )]($w.CollapsedMixin.html#collapse)
     * @snippet [HiddenMixin-hide.es6=Hide an element with no effect]
     * @snippet [HiddenMixin-hide_animation.es6=Hide an element with the "fade" effect]
     * @snippet [HiddenMixin-hide-FadeOptions.es6=Hide an element with the "fade" effect and custom options]
     * @snippet [HiddenMixin-hide_then.es6=Hide an element with an effect and log a message when the effect is done]
     * @snippet [HiddenMixin_toggle_hidden.es6=Toggle an element's hidden state]
     * @memberof $w.HiddenMixin
     */
    hide(effectName, effectOptions) {
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const {displayedOnlyComponents} = RMI.getComp(compId);
            const compDomIds = _.isEmpty(displayedOnlyComponents) ? [compId] : [...displayedOnlyComponents];

            if (this.hidden) {
                return resolve();
            }
            //TODO WEED-8265
            // if (shouldOverrideEditorPresets(effectName, effectOptions)) {
            //     return resetAnimationAndChangeElementVisibility(RMI, compId, true, resolve);
            // }

            //TODO WEED-8265 - for phase 1.1 only
            if (!effectName && _.isEmpty(effectOptions) || platformUtils.getEnv() === 'backend') {
                return changeElementVisibility(RMI, compId, true, resolve);
            }
            const functionData = EFFECTS.HIDE;
            const isValid = effectValidation.validateEffect(effectName, effectOptions, 'hide', this.id, functionData.deprecatedValues);
            if (!isValid) {
                return changeElementVisibility(RMI, compId, true, resolve);
            }

            const {presetName, params} = effectValidation.buildEffectObject(effectName, effectOptions, functionData);

            const initialCompIds = _.initial(compDomIds);
            const lastCompId = _.last(compDomIds);

            _.forEach(initialCompIds, (compId) => RMI.executeAnimation(compId, presetName, params));
             // Execute last comp in list with visibility callback
            RMI.executeAnimation(lastCompId, presetName, params, changeElementVisibility.bind(null, RMI, compId, true, resolve));
        });
    }

    /**
     * @member hidden
     * @label property
     * @summary Indicates if the element is visible or hidden.
     * @description
     *  If `hidden` is `true`, the element is not displayed on the page under
     *  any circumstances. A hidden element, unlike a
     *  [`collapsed`]($w.CollapsedMixin.html#collapsed) element, continues to
     *  take up the same space on the page as it did when it was visible.
     *
     *  If `hidden` is `false`, the element _may_ be displayed on the page.
     *
     *  However, an element whose `hidden` property is `false` is still not
     *  displayed if:
     *
     *  + It is [collapsed]($w.CollapsedMixin.html#collapsed).
     *  + Its [parent element]($w.Node.html#parent) is hidden or collapsed.
     *  + It is a slide in a [Slideshow]($w.Slideshow.html) which is
     *    currently not being displayed.
     *  + In Editor X, it has been marked as "Don't Display" for the current breakpoint.
     *
     *
     *  Even if the element is not displayed due to the conditions
     *  mentioned above, if its `hidden` property is set to `false`, it's
     *  displayed when the conditions no longer apply.
     *
     *  To determine if the element is actually visible, use the
     *  [`isVisible`](#isVisible) property.
     *
     *  To set the `hidden` property on an element, use the element's
     *  [`hide()`](#hide) or [`show()`](#show) functions.
     *
     *  If you select **Hidden on load** in the Properties and Events panel
     *  in the Editor, the `hidden` property is set to `true` when the page loads.
     *
     *  > **Note:** An element's `hidden` property is not the same as its
     *   [isVisible](#isVisible) property. The `hidden` property
     *   indicates whether the element _should_ be displayed, while `isVisible`
     *   indicates if it is actually displayed.
     * @snippet [HiddenMixin-hidden.es6=Get an element's hidden status]
     * @snippet [HiddenMixin_toggle_hidden.es6=Toggle an element's hidden state]
     * @type {Boolean}
     * @see [hide( )](#hide)
     * @see [show( )](#show)
     * @see [collapse( )]($w.CollapsedMixin.html#collapse)
     * @see [expand( )]($w.CollapsedMixin.html#expand)
     * @see [collapsed]($w.CollapsedMixin.html#collapsed)
     * @see [rendered]($w.Element.html#rendered)
     * @memberof $w.HiddenMixin
     * @attr {Boolean} hidden
     * @readonly
     */
    get hidden() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const props = RMI.getProps(compId);
        return !!props.isHidden;
    }

    /**
     * @function show
     * @summary Shows the element and sets its `hidden` property
     *  to `false`, using an effect if specified.
     * @description
     *  The `show()` function shows the element and returns a Promise that is
     *  resolved when the effect is complete and the element's [`hidden`](#hidden)
     *  property has been set to `false`.
     *
     *  You can optionally apply an effect when showing the element by providing
     *  an `effectName` value. You can also customize the effect by providing the
     *  optional `effectOptions` object.
     *
     *  Effects:
     *
     *  + `"arc"`
     *  + `"bounce"`
     *  + `"fade"`
     *  + `"flip"`
     *  + `"float"`
     *  + `"fly"`
     *  + `"fold"`
     *  + `"glide"`
     *  + `"puff"`
     *  + `"roll"`
     *  + `"slide"`
     *  + `"spin"`
     *  + `"turn"`
     *  + `"zoom"`
     * 
     * > **Notes:** 
     * > + In Editor X, `show ()` will not make an element visible for a given breakpoint if the element has been set to "Don't Display" for that breakpoint. Calling `show()` will however, change the `hidden` property of the element to `false`.
     * >
     * >
     * > + It is recommended not to mix `show` and `hide` with "Don't Display" in Editor X.
     * @param {String} [effectName] The name of the
     *  effect to play when showing the item.
     * @param {$w.EffectOptions~ArcEffectOptions | $w.EffectOptions~BounceEffectOptions | $w.EffectOptions~FadeEffectOptions | $w.EffectOptions~FlipEffectOptions | $w.EffectOptions~FloatEffectOptions | $w.EffectOptions~FlyEffectOptions | $w.EffectOptions~FoldEffectOptions | $w.EffectOptions~GlideEffectOptions | $w.EffectOptions~PuffEffectOptions | $w.EffectOptions~RollEffectOptions | $w.EffectOptions~SlideEffectOptions | $w.EffectOptions~SpinEffectOptions | $w.EffectOptions~TurnEffectOptions | $w.EffectOptions~ZoomEffectOptions} [effectOptions] The effect's options.
     * @returns {Promise}
     * @fulfill {void} When the effect is complete and the element's `hidden` property has been set to `false`.
     * @see [hide( )](#hide)
     * @see [hidden](#hidden)
     * @see [expand( )]($w.CollapsedMixin.html#expand)
     * @snippet [HiddenMixin-show.es6=Show an element with no effect]
     * @snippet [HiddenMixin-show_animation.es6=Show an element with the "fade" effect]
     * @snippet [HiddenMixin-show-FadeOptions.es6=Show an element with the "fade" effect and custom options]
     * @snippet [HiddenMixin-show_then.es6=Show an element with an effect and log a message when the effect is done]
     * @snippet [HiddenMixin_toggle_hidden.es6=Toggle an element's hidden state]
     * @memberof $w.HiddenMixin
     */
    show(effectName, effectOptions) {
        return new Promise((resolve) => {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const {displayedOnlyComponents} = RMI.getComp(compId);
            const compDomIds = _.isEmpty(displayedOnlyComponents) ? [compId] : [...displayedOnlyComponents];

            if (!this.hidden) {
                return resolve();
            }

            //TODO WEED-8265
            // if (shouldOverrideEditorPresets(effectName, effectOptions)) {
            //     return resetAnimationAndChangeElementVisibility(RMI, compId, false, resolve);
            // }

            //TODO WEED-8265 - for phase 1.1 only
            if (!effectName && _.isEmpty(effectOptions) || platformUtils.getEnv() === 'backend') {
                return changeElementVisibility(RMI, compId, false, resolve);
            }

            const functionData = EFFECTS.SHOW;
            const isValid = effectValidation.validateEffect(effectName, effectOptions, 'show', this.id, functionData.deprecatedValues);
            if (!isValid) {
                return changeElementVisibility(RMI, compId, false, resolve);
            }

            const {presetName, params} = effectValidation.buildEffectObject(effectName, effectOptions, functionData);

            const initialCompIds = _.initial(compDomIds);
            const lastCompId = _.last(compDomIds);

            _.forEach(initialCompIds, (compId) => RMI.executeAnimation(compId, presetName, params));
             // Execute last comp in list with visibility callback
            RMI.executeAnimation(lastCompId, presetName, params, changeElementVisibility.bind(null, RMI, compId, false, resolve));
        });
    }

    /**
     * @member isVisible
     * @label property
     * @syntax
     * get isVisible(): boolean
     * @summary Indicates if the element is actually visible.
     * @description
     *  If `isVisible` is `true`, the element is displayed on the page.
     *
     *  If `isVisible` is `false`, the element is not displayed on the page.
     *
     *  The value of the `isVisible` property is calculated based on the
     *  [`hidden`](#hidden), [`collapsed`](#collapsed), and [`rendered`]($w.Element#rendered)
     *  properties of the element and all of its ancestors. It is `true`
     *  only if the conditions exist in the element's property values and the
     *  property values of its ancestors such that the element is actually
     *  displayed on the page.
     * 
     * > **Notes:** 
     * > + An element's `isVisible` property is not the same as its
     *  [hidden](#hidden) property. The `isVisible` property
     *  indicates whether the element is actually displayed, while `hidden`
     *  indicates if it _should_ be displayed.
     * >
     * >
     * > + The `isVisible` property of an element remains `true` even if
     *  another element completely covers it so that a user cannot see it.
     * >
     * >
     * > + In Editor X, even if the `isVisible` property of the element is `true`, it will not be displayed if the element has been set to "Don't Display" for the current breakpoint.
     * @snippet [HiddenMixin-isVisible.es6=Get whether an element is visible]
     * @type {Boolean}
     * @memberof $w.HiddenMixin
     * @attr {Boolean} visible
     * @see [hidden](#hidden)
     * @see [collapsed]($w.CollapsedMixin.html#collapsed)
     * @see [rendered]($w.Element.html#rendered)
     * @readonly
     */
    get isVisible() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        if (RMI.isGhostComponent(compId)) {
            return false;
        }

        let parentId = compId;
        while (parentId){
            const parentProps = RMI.getProps(parentId);
            const parent = RMI.getComp(parentId);
            const isDisplayed = parent ? parent.isDisplayed : true;
            if (parentProps && (parentProps.isHidden || parentProps.isCollapsed || !isDisplayed)){
                return false;
            }
            parentId = RMI.getParent(parentId);
        }
        return true;
    }

    get isAnimatable() {
        return true;
    }

    /**
     * @namespace $w.EffectOptions
     * @summary Use effect options to customize an effect when [showing]($w.HiddenMixin.html#show) or [hiding]($w.HiddenMixin.html#hide) an element.
     *  Effect options include arcs, bouncing, fading, flipping, floating, flying in/out, folding, gliding and more.
     */

    /**
     * @typedef {Object} $w.EffectOptions~ArcEffectOptions
     * @summary An object used to customize the `"arc"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"left"`, `"right"`. Default: `"left"`.
     * @snippet [HiddenMixin-show-ArcOptions.es6=Show an element using the arc effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~BounceEffectOptions
     * @summary An object used to customize the `"bounce"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"topLeft"`, `"topRight"`, `"bottomRight"`,
     *  `"bottomLeft"`, `"center"`. Default: `"topLeft"`.
     * @property {String} intensity Valid values: `"soft"`, `"medium"`, `"hard"`. Default: `"medium"`.
     * @snippet [HiddenMixin-show-BounceOptions.es6=Show an element using the bounce effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~FadeEffectOptions
     * @summary An object used to customize the `"fade"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @snippet [HiddenMixin-show-FadeOptions.es6=Show an element using the fade effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~FlipEffectOptions
     * @summary An object used to customize the `"flip"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"top"`, `"right"`, `"bottom"`, `"left"`. Default: `"right"`.
     * @snippet [HiddenMixin-show-FlipOptions.es6=Show an element using the flip effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~FloatEffectOptions
     * @summary An object used to customize the `"float"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"top"`, `"right"`, `"bottom"`, `"left"`. Default: `"right"`.
     * @snippet [HiddenMixin-show-FloatOptions.es6=Show an element using the float effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~FlyEffectOptions
     * @summary An object used to customize the `"fly"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"top"`, `"right"`, `"bottom"`, `"left"`. Default: `"right"`.
     * @snippet [HiddenMixin-show-FlyOptions.es6=Show an element using the fly effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~FoldEffectOptions
     * @summary An object used to customize the `"fold"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"top"`, `"right"`, `"bottom"`, `"left"`. Default: `"left"`.
     * @snippet [HiddenMixin-show-FoldOptions.es6=Show an element using the fold effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~GlideEffectOptions
     * @summary An object used to customize the `"glide"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {Number} angle Valid values: `0`-`360` degrees. Default: `0`.
     * @property {Number} distance Valid values: `0`-`300` pixels. Default: `0`.
     * @snippet [HiddenMixin-show-GlideOptions.es6=Show an element using the glide effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~PuffEffectOptions
     * @summary An object used to customize the `"puff"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @snippet [HiddenMixin-show-PuffOptions.es6=Show an element using the puff effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~RollEffectOptions
     * @summary An object used to customize the `"roll"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"top"`, `"right"`, `"bottom"`, `"left"`. Default: `"left"`.
     * @snippet [HiddenMixin-show-RollOptions.es6=Show an element using the roll effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~SlideEffectOptions
     * @summary An object used to customize the `"slide"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"top"`, `"right"`, `"bottom"`, `"left"`. Default: `"left"`.
     * @snippet [HiddenMixin-show-SlideOptions.es6=Show an element using the slide effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~SpinEffectOptions
     * @summary An object used to customize the `"spin"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `cw`, `ccw`. Default: `cw`. 
     * @property {Number} cycles Valid values: `1`-`15`. Default: `5`.
     * @snippet [HiddenMixin-show-SpinOptions.es6=Show an element using the spin effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~TurnEffectOptions
     * @summary An object used to customize the `"turn"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @property {String} direction Valid values: `"right"`, `"left"`. Default: `"right"`.
     * @snippet [HiddenMixin-show-TurnOptions.es6=Show an element using the turn effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

    /**
     * @typedef {Object} $w.EffectOptions~ZoomEffectOptions
     * @summary An object used to customize the `"zoom"` effect.
     * @property {Number} duration Valid values: `0`-`4000` milliseconds. Default: `1200`.
     * @property {Number} delay Valid values: `0`-`8000` milliseconds. Default: `0`.
     * @snippet [HiddenMixin-show-ZoomOptions.es6=Show an element using the zoom effect and options]
     * @see [show( )]($w.HiddenMixin.html#show)
     * @see [hide( )]($w.HiddenMixin.html#hide)
     */

}

setAsyncFunctions(['hide', 'show']);
setStageUnfriendlyFunctions(HiddenMixin, 'hide');

export default HiddenMixin;
